import React, {CSSProperties, FC, useEffect, useState} from 'react';
import {createRoot} from 'react-dom/client';

import {sanitizeHref, tryParseUrl} from '../../libs/shared/core/src/helpers/browser/urlHelpers';
import {useTimeout} from '../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {reportToBugsnag} from '../../libs/shared/core/src/services/bugsnag/bugsnag';
import {fonts, fontSizes} from '../../libs/shared/ui/src/styles/fontStyles';

/*
 * Props.
 */

interface OpenInBrowserInterstitialProps {
  url: string;
}

/*
 * Constants.
 */

const closeMessageDelay = 3000;

/*
 * Style.
 */

const containerStyle: CSSProperties = {
  fontFamily: fonts.system,
  textAlign: 'center',
  marginTop: '10%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
};

const emailStyle: CSSProperties = {
  background: '#f3f4f6',
  borderRadius: '8px',
  padding: '8px',
  fontFamily: 'monospace',
  overflow: 'auto',
  fontSize: fontSizes.verySmall,
  margin: 0,
};

// Note: this component is rendered on its own in a new window. See `openInBrowser.ts`.
export const OpenInBrowserInterstitial: FC<OpenInBrowserInterstitialProps> = ({url}) => {
  const [isCloseMessageVisible, setIsCloseMessageVisible] = useState(false);
  const [setTimeout] = useTimeout();
  useEffect(() => {
    setTimeout(() => {
      setIsCloseMessageVisible(true);
    }, closeMessageDelay);
  }, [setTimeout]);

  const sanitizedHref = sanitizeHref(url);

  const emailAddress = maybeGetEmailAddress(sanitizedHref);

  return (
    <div style={containerStyle}>
      <p>
        Opening <a href={sanitizedHref}>link</a>...
      </p>

      {emailAddress !== undefined && (
        <>
          <p>If your email client does not open, try sending an email to</p>
          <p style={emailStyle}>{emailAddress}</p>
        </>
      )}

      {isCloseMessageVisible && (
        <p>
          <strong>You may now close this page.</strong>
        </p>
      )}
    </div>
  );
};

/*
 * Helper.
 */

export function tryRenderOpenInBrowserInterstitial(container: Element, url: string) {
  try {
    createRoot(container).render(<OpenInBrowserInterstitial url={url} />);
  } catch (error) {
    reportToBugsnag(error);
  }
}

function maybeGetEmailAddress(url: string) {
  const parsed = tryParseUrl(url);

  if (!parsed || !parsed.protocol.startsWith('mailto:')) {
    return undefined;
  }

  return parsed.pathname;
}
